import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, prefetchGet } from "../lib/api";
import { Layout } from "../components/Layout";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { StatCard } from "../components/badges";
import { num, fmtDate } from "../lib/format";
import { Link } from "react-router-dom";
import { Skeleton } from "../components/ui/skeleton";
import { ChevronRight, MapPin, AlertTriangle, RefreshCw } from "lucide-react";
import { CustomerLogo } from "../components/CustomerLogo";
import { useAuth } from "../context/AuthContext";

export default function Portfolio() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: clients, isLoading, error } = useQuery({
    queryKey: ["clients"],
    queryFn: async () => (await api.get("/clients")).data,
  });

  const totals = (clients || []).reduce(
    (a, c) => ({
      runs: a.runs + (c.run_count || 0),
      checks: a.checks + (c.check_count || 0),
      issues: a.issues + (c.issue_count || 0),
    }),
    { runs: 0, checks: 0, issues: 0 }
  );

  return (
    <Layout>
      <Breadcrumbs items={[{ label: "Portfolio" }]} />
      <div className="mt-3 flex items-end justify-between flex-wrap gap-4 border-b border-border pb-5">
        <div>
          <h1 className="font-heading text-4xl font-black">Customer Portfolio</h1>
          <p className="mt-1 text-muted-foreground">
            {user?.role === "admin"
              ? "All customers across the payroll book."
              : "Customers you are authorized to review."}
          </p>
        </div>
      </div>

      {/* Totals */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard testid="total-registers" label="Pay Registers" count={totals.runs}
          bar="bg-foreground/30" sub={`across ${clients?.length || 0} customers`} />
        <StatCard testid="total-checks" label="Checks Run" count={totals.checks}
          bar="bg-sky-500/60" sub="variance checks evaluated" />
        <StatCard
          testid="total-issues"
          label="Open Issues"
          count={totals.issues}
          accent={totals.issues > 0 ? "text-neg" : "text-pos"}
          bar={totals.issues > 0 ? "bg-neg" : "bg-pos"}
          sub={totals.issues > 0 ? "require reviewer attention" : "all clear"}
        />
      </div>

      {/* Customer grid */}
      <div className="mt-8">
        {isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-52 rounded-md" />
            ))}
          </div>
        )}

        {error && (
          <div className="rounded-md border border-neg/30 bg-neg/5 p-6 text-neg">
            Failed to load customers. {String(error.message)}
          </div>
        )}

        {clients && clients.length === 0 && (
          <div className="rounded-md border border-border bg-card p-12 text-center text-muted-foreground">
            No customers are assigned to your account yet.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {clients?.map((c, i) => (
            <Link
              key={c.payroll_client_GUID}
              to={`/clients/${c.payroll_client_GUID}`}
              data-testid={`client-card-${c.client_short_name}`}
              onMouseEnter={() => prefetchGet(qc, ["client", c.payroll_client_GUID], `/clients/${c.payroll_client_GUID}`)}
              onFocus={() => prefetchGet(qc, ["client", c.payroll_client_GUID], `/clients/${c.payroll_client_GUID}`)}
              style={{ animationDelay: `${i * 50}ms` }}
              className="group animate-fade-up press rounded-md border border-border bg-card p-6 transition-all hover:border-foreground/40 hover:shadow-sm hover:-translate-y-0.5"
            >
              <div className="flex items-start justify-between">
                <CustomerLogo clientId={c.payroll_client_GUID} hasLogo={c.has_logo} size={44} iconSize={22} />
                {c.issue_count > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-sm border border-neg/30 bg-neg/10 px-2 py-0.5 text-xs font-semibold text-neg">
                    <AlertTriangle size={12} /> {num(c.issue_count)} issues
                  </span>
                )}
              </div>

              <h3 className="mt-4 font-heading text-xl font-bold leading-tight">{c.client_name}</h3>
              <div className="mt-1 font-mono text-xs uppercase tracking-wide text-foreground/80" data-testid={`client-code-${c.client_short_name}`}>
                {c.client_short_name}
                {c.provider_company_code ? ` · Co ${c.provider_company_code}` : ""}
              </div>
              <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground font-mono uppercase tracking-wide">
                <MapPin size={12} /> {c.country_code} · {c.default_currency_code}
              </div>
              {c.last_refreshed ? (
                <div className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground/80" data-testid={`client-refreshed-${c.client_short_name}`}>
                  <RefreshCw size={11} /> Refreshed {fmtDate(c.last_refreshed)}
                </div>
              ) : null}

              <div className="mt-5 grid grid-cols-3 gap-2 border-t border-border pt-4">
                <div>
                  <div className="font-mono text-2xl font-bold">{num(c.run_count)}</div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Registers</div>
                </div>
                <div>
                  <div className="font-mono text-2xl font-bold">{num(c.check_count)}</div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Checks</div>
                </div>
                <div>
                  <div className={`font-mono text-2xl font-bold ${c.issue_count > 0 ? "text-neg" : "text-pos"}`}>
                    {num(c.issue_count)}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Issues</div>
                </div>
              </div>

              <div className="mt-4 flex items-center gap-1 text-sm font-semibold text-muted-foreground group-hover:text-foreground transition-colors">
                View summary <ChevronRight size={16} className="transition-transform group-hover:translate-x-0.5" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </Layout>
  );
}
